import React from 'react'
import {
  DollarSign,
  TrendingUp,
  Target,
  Sparkles,
  CheckCircle,
  ArrowRight,
  BarChart3,
  Zap,
  Info
} from 'lucide-react'

// 定价卡片组件
const PricingCard: React.FC<{
  title: string
  price: number
  description: string
  features: string[]
  recommended?: boolean
  color: string
  icon: React.ReactNode
}> = ({ title, price, description, features, recommended, color, icon }) => (
  <div
    className={`relative bg-white rounded-2xl overflow-hidden transition-all hover:-translate-y-1 ${
      recommended
        ? 'border-2 border-emerald-400 shadow-xl shadow-emerald-100/50'
        : 'border border-slate-200 hover:shadow-lg'
    }`}
  >
    {/* 推荐标签 */}
    {recommended && (
      <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold rounded-full shadow-lg">
        ⭐ 推荐
      </div>
    )}

    {/* 顶部渐变 */}
    <div className={`h-2 bg-gradient-to-r ${color}`} />

    <div className="p-6">
      {/* 图标和标题 */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-lg`}
        >
          {icon}
        </div>
        <div>
          <h4 className="font-bold text-slate-900">{title}</h4>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
      </div>

      {/* 价格 */}
      <div className="mb-5">
        <div className="flex items-baseline gap-1">
          <span className="text-sm text-slate-500">¥</span>
          <span
            className={`text-4xl font-bold ${recommended ? 'text-emerald-600' : 'text-slate-800'}`}
          >
            {price}
          </span>
          <span className="text-sm text-slate-500">/课时</span>
        </div>
      </div>

      {/* 特点列表 */}
      <div className="space-y-2 mb-5">
        {features.map((feature, i) => (
          <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
            <CheckCircle
              className={`w-4 h-4 flex-shrink-0 ${recommended ? 'text-emerald-500' : 'text-slate-400'}`}
            />
            {feature}
          </div>
        ))}
      </div>

      {/* 按钮 */}
      <button
        className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all ${
          recommended
            ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-200/50 hover:shadow-xl'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
        }`}
      >
        {recommended ? '应用此定价' : '了解更多'}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </div>
)

export const TeacherPricingSuggestions: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        <div className="relative px-8 py-10 text-center">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
            <DollarSign className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">智能定价建议</h2>
          <p className="text-white/80">基于市场数据的智能定价策略</p>
        </div>
      </div>

      {/* 市场概览 */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center text-white mx-auto mb-3 shadow-lg">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-slate-900">¥230</div>
          <div className="text-sm text-slate-500">市场均价</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white mx-auto mb-3 shadow-lg">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-slate-900">+8%</div>
          <div className="text-sm text-slate-500">您的溢价空间</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center text-white mx-auto mb-3 shadow-lg">
            <Zap className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-slate-900">高</div>
          <div className="text-sm text-slate-500">定价竞争力</div>
        </div>
      </div>

      {/* 定价方案 */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center text-white shadow-lg">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">推荐定价方案</h3>
            <p className="text-sm text-slate-500">根据您的背景和市场情况生成</p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <PricingCard
            title="入门级"
            price={150}
            description="吸引新学生"
            features={['适合新手教师', '快速积累评价', '建立口碑', '低门槛获客']}
            color="from-blue-400 to-indigo-500"
            icon={<Target className="w-6 h-6" />}
          />
          <PricingCard
            title="标准级"
            price={250}
            description="最优性价比"
            features={['基于您的经验', '符合市场定位', '平衡收益与获客', '推荐首选方案']}
            recommended
            color="from-emerald-400 to-teal-500"
            icon={<TrendingUp className="w-6 h-6" />}
          />
          <PricingCard
            title="高级"
            price={400}
            description="高端定位"
            features={['专业课程定价', '高级教师专属', '精准客户群', '高价值服务']}
            color="from-purple-400 to-pink-500"
            icon={<DollarSign className="w-6 h-6" />}
          />
        </div>
      </div>

      {/* 提示 */}
      <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-bold text-amber-800 mb-1">定价建议</h4>
            <p className="text-sm text-amber-700">
              建议新教师从入门级价格开始，积累5-10个好评后逐步提升至标准级。定价策略应根据课程质量、学生反馈和市场变化定期调整。
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
